// Dependencies
import React, { PropsWithChildren, useMemo } from "react";
import { matchRoutes, useNavigate } from "react-router-dom";

// Project Imports
import { router } from "../../app/router";

// Styles
import css from "./Link.module.scss";

interface LinkProps {
	href: string;
	style?: React.CSSProperties;
	forceHardReload?: boolean;
	className?: string;
	overrideStyles?: boolean;
	/**
	 * If true, this will open a link in a new window.
	 * @default false
	 */
	isExternal?: boolean;
	/**
	 * remove underline under link
	 */
	noUnderline?: boolean;

	/**
	 * hide link card
	 */
	hideInternalCard?: boolean;

	/**
	 * test id for link
	 */
	testId?: string;
}

/**
 * @deprecated Use the Link component from the py-components package instead
 */
const Link = (props: PropsWithChildren<LinkProps>) => {
	const { href, children, noUnderline = false, testId = "link" } = props;

	const navigate = useNavigate();
	const isSpaLink = useMemo(() => {
		const matches = matchRoutes(router.routes, href);

		// the first match is always the base match that renders the <App />
		if (!matches || matches.length <= 1) {
			return false;
		}

		return true;
	}, [href]);

	const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
		// cancel if the link is external
		if (props.isExternal) {
			return;
		}

		if (href) {
			if (!props.forceHardReload && !e.defaultPrevented && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
				e.preventDefault();
				if (isSpaLink) {
					navigate(href);
				} else {
					document.location.href = href;
				}
			}
		}
	};

	return (
		<a
			rel={props.isExternal ? "noopener noreferrer" : undefined}
			target={props.isExternal ? "_blank" : undefined}
			style={props.style}
			className={`${css.link} ${props.className} ${props.overrideStyles ? css.overrideStyles : ""} ${
				noUnderline && css.noUnderline
			} ${props.hideInternalCard ? "no-tooltip" : ""}`}
			href={href}
			onClick={handleClick}
			data-testid={testId}
		>
			{children}
		</a>
	);
};

/** @deprecated Use the Link component from the py-components package instead */
export default Link;
